<template>
	<div class="sidebar-accordion" :data-open="isOpen" :data-pinned="!! pinned">
		<button type="button" class="nav-item accordion-trigger" :class="{ active: active && ! isOpen }" @click="toggle">
			<div class="icon-box" :class="iconBgClass ?? 'bg-vert-sylvestre'" :data-custom-bg="!!iconBgClass">
				<cfl-icon :icon="icon"></cfl-icon>
			</div>
			<span class="label">{{ title }}</span>
			<template v-if="! pinned">
				<cfl-icon icon="action-caret-right" class="chevron"></cfl-icon>
			</template>
		</button>

		<template v-if="isOpen">
			<div class="accordion-body">
				<slot></slot>
			</div>
		</template>
	</div>
</template>

<script lang="ts">
	import { reactive } from "vue";

	/**
	 * État utilisateur d'expansion par accordion. 3 valeurs :
	 *   - "expanded"  → l'user a forcé ouvert
	 *   - "collapsed" → l'user a forcé fermé
	 *   - undefined   → pas d'override, on suit `props.active` (ouvert auto si la cat est active)
	 *
	 * Partagé au niveau module (déclaré dans un `<script>` SANS `setup`, exécuté UNE SEULE FOIS
	 * à l'import) car `layout-admin-sidebar` est inclus dans chaque page admin via
	 * `layout-admin-base` → démonté + remonté à chaque navigation. Un `ref` dans `<script setup>`
	 * repartirait à `undefined` à chaque mount.
	 */
	type ToggleState = "expanded" | "collapsed";
	const userToggle = reactive<Record<string, ToggleState>>({});
</script>

<script lang="ts" setup>
	import { computed } from "vue";
	import CflIcon from "@shared-components-cfl/cfl-icon.vue";
	import IconType from "@shared-types/icon.type";

	const props = defineProps<{
		title: string,
		icon: IconType,
		/** Vrai si la route courante appartient à cette catégorie. Ouverture auto sauf override user. */
		active: boolean,
		/** Classes Tailwind optionnelles pour customiser le bg de l'icon-box (defaut : vert-sylvestre).
		 * Ex : "bg-marron-sepia dark:bg-marron-sepia-alt" pour Système. */
		iconBgClass?: string,
		/** Toujours déployé, le clic ne le replie pas : pour la catégorie qu'on consulte en permanence. */
		pinned?: boolean,
	}>();

	const isOpen = computed(() =>
	{
		if (props.pinned) return true;

		const t = userToggle[props.title];
		if (t === "expanded") return true;
		if (t === "collapsed") return false;
		return props.active;
	});

	const toggle = () =>
	{
		if (props.pinned) return;

		userToggle[props.title] = isOpen.value ? "collapsed" : "expanded";
	};
</script>

<style scoped>
	@reference "@main-css";

	.sidebar-accordion {
		@apply relative flex flex-col;
		@apply border-b border-white/15;
		@apply last:border-b-0;

		.accordion-trigger {
			@apply relative flex items-center gap-3 px-3 py-2.5 w-full;
			@apply bg-transparent border-0 cursor-pointer text-left;
			@apply text-base text-white;
			@apply hover:bg-vert-sylvestre dark:hover:bg-vert-de-hooker;

			&.active {
				@apply bg-vert-anthracite;

				&::before {
					content: "";
					@apply absolute left-0 top-1.5 bottom-1.5 w-[2px] rounded-r-sm bg-white;
				}
			}

			.icon-box {
				/* bg-color appliqué via :class="iconBgClass ?? 'bg-vert-sylvestre'" sur le template */
				@apply w-8 h-8 rounded-md shrink-0;
				@apply flex items-center justify-center;
				@apply border border-white/15;
				@apply text-white/80;
				@apply transition-colors;

				/* En dark mode, les bg custom utilisent leur variant -alt (couleurs claires) →
				 * l'icône doit passer en sombre pour conserver le contraste. */

				&[data-custom-bg="true"] {
					@apply dark:text-black/80;
				}

				&:deep([data-icon]) {
					@apply w-4 h-4 shrink-0;
				}
			}

			&:hover .icon-box {
				/* Conserve la couleur affectée (custom ou default vert-sylvestre), éclaircie de 20%. */
				@apply brightness-125;

				&:deep([data-icon]) {
					@apply rotate-icon;
				}
			}

			.label {
				@apply min-w-0 flex-1 truncate;
			}

			.chevron {
				@apply opacity-50 w-4 h-4 shrink-0;
				transition: transform 0.2s ease-in-out;
			}
		}

		&[data-open="true"] {
			.accordion-trigger {
				@apply font-semibold;

				.chevron {
					transform: rotate(90deg);
				}
			}
		}

		&[data-pinned="true"] {
			.accordion-trigger {
				@apply cursor-default;
			}
		}

		.accordion-body {
			/* ml-[28px] aligne la border-l avec le centre vertical de l'icône du trigger :
			 * accordion-trigger px-3 (12px) + icon-box w-8 / 2 (16px) = 28px. */
			@apply flex flex-col gap-0.5 pt-1 ml-[28px];
			@apply border-l border-white/15;
		}
	}
</style>
